import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { linkControlledHoverEffect } from '@hivebrite/design-system/Theme';
import { onEnterOrSpacePress } from '@hivebrite/utils';

import ClampedLinkWorkaround from 'hb-react/shared/components/DesignSystem/ClampedLinkWorkaround';

import type { NewsCardPinnedProps } from './types';

export const NewsCardPinned = ({
  coverPicture,
  fallbackCoverPicture,
  onClick,
  subject,
}: NewsCardPinnedProps) => (
  <CardStyled
    onClick={onClick}
    onKeyDown={onEnterOrSpacePress(onClick)}
    role="link"
    tabIndex={0}
  >
    <ImgWrapper>
      <ImgStyled
        as="img"
        onError={(e) => {
          if (fallbackCoverPicture) {
            (e.target as HTMLImageElement).src = fallbackCoverPicture;
          }
        }}
        src={coverPicture}
      />
    </ImgWrapper>
    <Box
      pb={6}
      pt={3}
      px={{ _: 3, l: 4 }}
      zIndex={1}
      data-testid="link-to-news"
    >
      <ScreenReadersContent>
        {s__('Accessibility|Click to see the news %{subject}').i__({
          subject,
        })}
      </ScreenReadersContent>
      <LinkStyled>
        <ClampedLinkWorkaround
          clamp={3}
          lineHeight="2rem"
          margin={0}
          padding={0}
        >
          {subject}
        </ClampedLinkWorkaround>
      </LinkStyled>
    </Box>
  </CardStyled>
);

const ImgStyled = styled(Box).attrs({
  alt: '',
  bg: 'white',
  width: '100%',
})`
  cursor: pointer;
  object-fit: cover;
  aspect-ratio: 1600 / 600;
`;

const ImgWrapper = styled(Box).attrs({
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
})`
  background-image: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.6));
  border-radius: ${({ theme }) => {
    const br = theme.themeVariables.globalBorderRadius;

    return `${br}px ${br}px 0 0`;
  }};
`;

const LinkStyled = styled(Box).attrs({
  as: 'h4',
  font: 'h4-semibold',
})`
  text-decoration: none;
`;

const CardStyled = styled(Card)`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  padding: 0;
  position: relative;
  height: 100%;

  @media (hover: hover) {
    &:hover ${LinkStyled} {
      ${linkControlledHoverEffect()}
    }
  }
`;
